import PropTypes from 'prop-types';
import { useController } from 'react-hook-form';
import { HelpCircle } from 'lucide-react';

import { cn } from '#/lib/utils';
import DateInputCore from '#/modules/commonComponents/form/DateInputCore';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';

function FloatingDateLabel({ id, label, tip }) {
  if (!tip) {
    return (
      <label htmlFor={id} className="input_label">
        {label}
      </label>
    );
  }

  return (
    <label htmlFor={id} className="input_label">
      <span className="inline-flex items-center gap-0.5">
        <span>{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="
                text-gray-400 inline-flex cursor-help items-center border-0 bg-transparent p-0
                hover:text-primary-600
              "
              aria-label={`${label}說明`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <HelpCircle size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {tip}
          </TooltipContent>
        </Tooltip>
      </span>
    </label>
  );
}

FloatingDateLabel.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
};

function FloatingDateFieldShell({
  id,
  label,
  tip,
  mode,
  value,
  onChange,
  onBlur,
  placeholder,
  containerClassName,
  disabled,
  readOnly,
  fieldError,
  numberOfMonths,
  collapseEmptyError,
}) {
  const hasValue = mode === 'range'
    ? Boolean(value?.from || value?.to)
    : Boolean(value);

  return (
    <div className={cn('input_container', containerClassName)}>
      <div className="relative">
        <DateInputCore
          id={id}
          mode={mode}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={hasValue ? ' ' : placeholder}
          disabled={disabled}
          readOnly={readOnly}
          ariaInvalid={Boolean(fieldError)}
          numberOfMonths={numberOfMonths}
        />
        <FloatingDateLabel id={id} label={label} tip={tip} />
      </div>
      {collapseEmptyError && !fieldError ? null : (
        <p className="form_error">
          {fieldError ? fieldError.message || null : null}
        </p>
      )}
    </div>
  );
}

FloatingDateFieldShell.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  mode: PropTypes.oneOf(['single', 'range']).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  containerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  fieldError: PropTypes.object,
  numberOfMonths: PropTypes.number,
  collapseEmptyError: PropTypes.bool,
};

function FloatingDateInputRhf({
  id,
  label,
  tip,
  control,
  errors,
  mode,
  placeholder,
  containerClassName,
  disabled,
  readOnly,
  numberOfMonths,
  collapseEmptyError,
}) {
  const { field } = useController({
    name: id,
    control,
    defaultValue: mode === 'range' ? { from: '', to: '' } : '',
  });
  const fieldError = getNestedError(errors, id);

  return (
    <FloatingDateFieldShell
      id={id}
      label={label}
      tip={tip}
      mode={mode}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      placeholder={placeholder}
      containerClassName={containerClassName}
      disabled={disabled}
      readOnly={readOnly}
      fieldError={fieldError}
      numberOfMonths={numberOfMonths}
      collapseEmptyError={collapseEmptyError}
    />
  );
}

FloatingDateInputRhf.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  mode: PropTypes.oneOf(['single', 'range']).isRequired,
  placeholder: PropTypes.string,
  containerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  numberOfMonths: PropTypes.number,
  collapseEmptyError: PropTypes.bool,
};

function FloatingDateInputControlled({
  id,
  label,
  tip,
  value,
  onChange,
  onBlur,
  errors,
  mode,
  placeholder,
  containerClassName,
  disabled,
  readOnly,
  numberOfMonths,
  collapseEmptyError,
}) {
  const fieldError = getNestedError(errors, id);
  const resolvedValue = value ?? (mode === 'range' ? { from: '', to: '' } : '');

  return (
    <FloatingDateFieldShell
      id={id}
      label={label}
      tip={tip}
      mode={mode}
      value={resolvedValue}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      containerClassName={containerClassName}
      disabled={disabled}
      readOnly={readOnly}
      fieldError={fieldError}
      numberOfMonths={numberOfMonths}
      collapseEmptyError={collapseEmptyError}
    />
  );
}

FloatingDateInputControlled.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  errors: PropTypes.object,
  mode: PropTypes.oneOf(['single', 'range']).isRequired,
  placeholder: PropTypes.string,
  containerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  numberOfMonths: PropTypes.number,
  collapseEmptyError: PropTypes.bool,
};

/**
 * Floating 日期欄：手打 + Calendar；single value = API `YYYY-MM-DD`。
 * - RHF：傳 `control`（＋可選 `errors`）
 * - controlled：傳 `value` + `onChange`（無 `control`）
 */
const FloatingDateInput = ({
  id,
  label,
  tip,
  control,
  errors,
  value,
  onChange,
  onBlur,
  mode = 'single',
  placeholder = '西元年/月/日',
  containerClassName = '',
  disabled = false,
  readOnly = false,
  numberOfMonths,
  /** true：沒有錯誤時不保留 .form_error 高度（非表單情境，如主控台篩選） */
  collapseEmptyError = false,
}) => {
  if (control) {
    return (
      <FloatingDateInputRhf
        id={id}
        label={label}
        tip={tip}
        control={control}
        errors={errors}
        mode={mode}
        placeholder={placeholder}
        containerClassName={containerClassName}
        disabled={disabled}
        readOnly={readOnly}
        numberOfMonths={numberOfMonths}
        collapseEmptyError={collapseEmptyError}
      />
    );
  }

  return (
    <FloatingDateInputControlled
      id={id}
      label={label}
      tip={tip}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      errors={errors}
      mode={mode}
      placeholder={placeholder}
      containerClassName={containerClassName}
      disabled={disabled}
      readOnly={readOnly}
      numberOfMonths={numberOfMonths}
      collapseEmptyError={collapseEmptyError}
    />
  );
};

FloatingDateInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  control: PropTypes.object,
  errors: PropTypes.object,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  mode: PropTypes.oneOf(['single', 'range']),
  placeholder: PropTypes.string,
  containerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  numberOfMonths: PropTypes.number,
  collapseEmptyError: PropTypes.bool,
};

export default FloatingDateInput;
